import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useClipboard } from '../hooks/useClipboard';
import { socketService } from '../services/socketService';

const { FiCopy, FiCheck, FiClock, FiMonitor, FiDownload, FiFile, FiExternalLink, FiMessageSquare, FiSend } = FiIcons;

export default function ClipboardItem({ item, index }) {
  const [copied, setCopied] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const { copyToClipboard } = useClipboard();
  const myDeviceId = socketService.getDeviceId();

  const handleCopy = async () => {
    const textToCopy = item.type === 'text' ? item.content : item.fileName || item.content;
    const success = await copyToClipboard(textToCopy);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleAddComment = () => {
    if (!commentText.trim()) return;
    const socket = socketService.getSocket();
    if (socket) {
      socket.emit('add_item_comment', { 
        room: item.room_name, 
        itemId: item.id, 
        comment: commentText.trim() 
      });
      setCommentText('');
    }
  };

  const formatSize = (bytes) => {
    if (!bytes) return '';
    const mb = bytes / (1024 * 1024);
    return mb < 1 ? `${(bytes / 1024).toFixed(1)} KB` : `${mb.toFixed(1)} MB`;
  };

  const renderContent = () => {
    if (item.type === 'image') {
      return (
        <div className="space-y-3">
          <div className="relative group/img overflow-hidden rounded-xl border border-gray-100 bg-gray-50">
            <img src={item.content} alt={item.fileName} className="max-h-80 w-auto object-contain mx-auto transition-transform duration-500 group-hover/img:scale-[1.02]" loading="lazy" />
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover/img:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <a href={item.content} target="_blank" rel="noopener noreferrer" className="p-2 bg-white rounded-lg text-gray-900 hover:bg-gray-100">
                <SafeIcon icon={FiExternalLink} className="w-5 h-5" />
              </a>
            </div>
          </div>
          <p className="text-xs font-bold text-gray-500 truncate flex items-center gap-1.5">
            <SafeIcon icon={FiFile} className="w-3 h-3" /> {item.fileName} ({formatSize(item.fileSize)})
          </p>
        </div>
      );
    }
    if (item.type === 'file') {
      return (
        <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 flex items-center gap-4 group/file">
          <div className="w-12 h-12 bg-white rounded-lg shadow-sm flex items-center justify-center text-blue-600">
            <SafeIcon icon={FiFile} className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-900 truncate">{item.fileName}</p>
            <p className="text-xs text-gray-500 mt-0.5">{formatSize(item.fileSize)} • {item.fileType?.split('/')[1]?.toUpperCase() || 'FILE'}</p>
          </div>
          <a href={item.content} download={item.fileName} className="p-2.5 bg-white border border-gray-200 rounded-lg text-gray-600 hover:text-blue-600 hover:border-blue-200 transition-all shadow-sm">
            <SafeIcon icon={FiDownload} className="w-4 h-4" />
          </a>
        </div>
      );
    }
    return <div className="text-gray-900 text-[15px] leading-relaxed break-words font-medium">{item.content}</div>;
  };

  return (
    <motion.div 
      className="bg-white rounded-3xl border border-gray-100 p-6 shadow-sm group hover:shadow-md hover:border-blue-100 transition-all duration-300"
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.05 }}
      layout
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {renderContent()}
          <div className="flex items-center gap-4 mt-5">
            <div className="flex items-center gap-1.5 text-[10px] font-black text-gray-400 uppercase tracking-tighter">
              <SafeIcon icon={FiClock} className="w-3 h-3" />
              <span>{formatDistanceToNow(new Date(item.timestamp), { addSuffix: true })}</span>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] font-black text-blue-500/70 uppercase tracking-tighter">
              <SafeIcon icon={FiMonitor} className="w-3 h-3" />
              <span>{item.device_name}</span>
            </div>
            <button 
              onClick={() => setShowComments(!showComments)}
              className={`flex items-center gap-1.5 text-[10px] font-black uppercase tracking-tighter transition-colors ${showComments ? 'text-blue-600' : 'text-gray-400 hover:text-blue-500'}`}
            >
              <SafeIcon icon={FiMessageSquare} className="w-3.5 h-3.5" />
              <span>{item.comments?.length || 0} {item.comments?.length === 1 ? 'Annotation' : 'Annotations'}</span>
            </button>
          </div>
        </div>
        <motion.button 
          onClick={handleCopy}
          className={`shrink-0 w-11 h-11 rounded-2xl flex items-center justify-center transition-all duration-300 shadow-sm ${copied ? 'bg-green-600 text-white' : 'bg-gray-50 text-gray-400 group-hover:bg-blue-600 group-hover:text-white'}`}
          whileHover={{ scale: 1.1, rotate: 5 }}
          whileTap={{ scale: 0.9 }}
        >
          <SafeIcon icon={copied ? FiCheck : FiCopy} className="w-4.5 h-4.5" />
        </motion.button>
      </div>

      <AnimatePresence>
        {showComments && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="mt-6 pt-6 border-t border-gray-50 flex flex-col gap-3">
              {item.comments?.map((c, i) => (
                <div key={c.id} className="flex gap-3 items-start bg-gray-50/50 p-3 rounded-2xl border border-gray-100/50">
                  <div className={`w-8 h-8 rounded-lg shrink-0 flex items-center justify-center text-xs font-black ${c.senderId === myDeviceId ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-500'}`}>
                    {c.senderId.substring(0, 2).toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700 leading-snug">{c.content}</p>
                    <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                      {c.senderId === myDeviceId ? 'You' : c.senderId} • {formatDistanceToNow(new Date(c.timestamp), { addSuffix: true })}
                    </p>
                  </div>
                </div>
              ))}
              <div className="flex gap-2 mt-2">
                <input 
                  type="text"
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleAddComment()}
                  placeholder="Annotate this clip..."
                  className="flex-1 bg-white border border-gray-200 rounded-xl px-4 py-2.5 text-xs font-medium outline-none focus:border-blue-500 transition-all shadow-sm"
                />
                <button 
                  onClick={handleAddComment}
                  className="w-10 h-10 bg-gray-900 text-white rounded-xl flex items-center justify-center hover:bg-black transition-colors"
                >
                  <SafeIcon icon={FiSend} className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}